import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, type UploadProgress } from '@/lib/api-client';

export type PosAppVersionStatus = 'testing' | 'published' | 'archived';

/**
 * The applications released through Super Admin. Each has its own API
 * namespace and its own version history; nothing here is shared between them
 * but the shape of a row.
 */
export type ReleaseApp = 'pos' | 'mobile' | 'cds';

const ROUTE_SLUG: Record<ReleaseApp, string> = {
  pos: 'pos-app-versions',
  mobile: 'mobile-app-versions',
  cds: 'cds-app-versions',
};

export interface PosAppVersion {
  id: string;
  appId: string;
  packageName: string;
  versionName: string;
  versionCode: number;
  apkFileName: string;
  fileSizeBytes: number;
  sha256: string;
  releaseNotes: string | null;
  status: PosAppVersionStatus;
  uploadedByName: string | null;
  uploadedAt: string;
  publishedByName: string | null;
  publishedAt: string | null;
  archivedAt: string | null;
}

interface VersionListResponse {
  versions: PosAppVersion[];
  /** The one build devices are being offered, or null when none is live yet. */
  published: PosAppVersion | null;
}

/** Per app, so a mutation on one tab never refetches — or shows — the other's list. */
const keyFor = (app: ReleaseApp) => [ROUTE_SLUG[app]];
const adminBase = (app: ReleaseApp) => `/admin/${ROUTE_SLUG[app]}`;

export function usePosAppVersions(app: ReleaseApp = 'pos') {
  return useQuery({
    queryKey: keyFor(app),
    queryFn: () => api.get<VersionListResponse>(adminBase(app)),
  });
}

/**
 * Upload a build. The server reads the package, version and signing
 * certificate out of the APK itself, so the fields sent here are a
 * cross-check — a mismatch means the wrong file was picked.
 */
export function useUploadPosAppVersion(app: ReleaseApp = 'pos') {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      file,
      versionName,
      versionCode,
      releaseNotes,
      onProgress,
    }: {
      file: File;
      versionName: string;
      versionCode: string;
      releaseNotes: string;
      /** Bytes sent so far — the page turns it into a progress bar. */
      onProgress?: UploadProgress;
    }) => {
      const form = new FormData();
      // Fields before the file: @fastify/multipart exposes earlier fields on
      // the file part, and reads the stream once.
      form.append('versionName', versionName);
      form.append('versionCode', versionCode);
      form.append('releaseNotes', releaseNotes);
      form.append('file', file);
      return api.postFormWithProgress<{ version: PosAppVersion }>(adminBase(app), form, onProgress);
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: keyFor(app) }),
  });
}

export function usePublishPosAppVersion(app: ReleaseApp = 'pos') {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.post<{ version: PosAppVersion }>(`${adminBase(app)}/${id}/publish`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: keyFor(app) }),
  });
}

/**
 * Delete a build outright — the row and the APK in S3. The server refuses the
 * published version; archived and testing builds are fair game.
 */
export function useDeletePosAppVersion(app: ReleaseApp = 'pos') {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      api.delete<{ deleted: { id: string; versionName: string; versionCode: number } }>(`${adminBase(app)}/${id}`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: keyFor(app) }),
  });
}

export function useArchivePosAppVersion(app: ReleaseApp = 'pos') {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.post<{ version: PosAppVersion }>(`${adminBase(app)}/${id}/archive`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: keyFor(app) }),
  });
}

/**
 * Where the browser fetches a build from.
 *
 * A plain link rather than a fetch: the session cookie rides along on a
 * same-origin request, and the browser streams a 200 MB APK to disk instead of
 * holding it in memory as a blob.
 */
export function posAppVersionDownloadUrl(id: string, app: ReleaseApp = 'pos'): string {
  return `/api/v1${adminBase(app)}/${id}/download`;
}
